import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import _isEmpty from 'lodash/isEmpty';
import _keyBy from 'lodash/keyBy';
import { CONTENT_TYPE, CONTENT_TYPE_ID, INITIAL_EXERCISE_CATEGORY_FORM } from '../contants';
import { ErrorToast, MultipleErrorToast, notifySuccess } from '../../utils/Utils';
import { setExerciseCategories, setExerciseCategory, showProgress } from '../../../redux/actions/DealershipActions';
import { apiEndpoints } from '../../../constants/apiEndpoints';
import service from '../../../apis/service';

export const useExerciseCategory = () => {
  const [form, setForm] = useState(INITIAL_EXERCISE_CATEGORY_FORM);
  const [imageData, setImageData] = useState();
  const [thumbnail, setThumbnail] = useState('');
  const [thumbnailImg, setThumbnailImg] = useState(null);
  const [departmentsList, setDepartmentsList] = useState([]);
  const [rnpList, setRnpList] = useState({});
  const [selectedRnp, setSelectedRnp] = useState('');
  const [contentType, setContentType] = useState(CONTENT_TYPE[0].value);

  const dispatch = useDispatch();
  const selectedCat = useSelector((state) => state.aiRoleplayData.selectedCategory || {});

  const handleDepartmentChange = (selected) => {
    setForm((prev) => ({
      ...prev,
      departmentIds: selected,
    }));
  };

  const handleForm = (e, type, limit) => {
    const input = e.target.value;

    if (limit) {
      if (input.length > limit) {
        return;
      }
    }

    const numberTypes = ['beginnerUnlockScore', 'intermediateUnlockScore', 'advanceUnlockScore'];
    const isAllowedType = numberTypes.includes(type);

    const newValue = isAllowedType ? input.replace(/\D/g, '') : input;

    setForm((pre) => ({
      ...pre,
      [type]: newValue,
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    setImageData(file);
    const reader = new global.FileReader();
    reader.onload = (ev) => {
      setThumbnailImg(ev.target.result);
    };
    reader.readAsDataURL(file);
  };

  const handleDeleteThumbnail = () => {
    setImageData(undefined);
    setThumbnail('');
    setThumbnailImg(null);
  };

  const handleValidation = (isEdit) => {
    const { name, beginnerUnlockScore, intermediateUnlockScore, advanceUnlockScore, subtext, departmentIds } = form;
    let isValid = true;
    const errors = [];
    if (
      !name ||
      (!imageData && !isEdit) ||
      beginnerUnlockScore == null ||
      intermediateUnlockScore == null ||
      advanceUnlockScore == null ||
      !subtext ||
      !departmentIds?.length
    ) {
      isValid = false;
      ErrorToast('Please enter all mandatory info before you save');
      return isValid;
    }
    if (parseInt(contentType, 10) !== CONTENT_TYPE_ID.ROCKED && selectedRnp === '') {
      isValid = false;
      ErrorToast('Please enter all mandatory info before you save');
      return isValid;
    }

    if (errors.length !== 0) {
      MultipleErrorToast(errors);
    }
    return isValid;
  };

  const handleSave = async (onClose, isEdit) => {
    const isValid = handleValidation(isEdit);
    if (!isValid) return;

    dispatch(showProgress(true));
    const { name, beginnerUnlockScore, intermediateUnlockScore, advanceUnlockScore, subtext, id, departmentIds } = form;
    const data = new global.FormData();
    data.append('name', name);
    data.append('beginnerUnlockScore', beginnerUnlockScore);
    data.append('intermediateUnlockScore', intermediateUnlockScore);
    data.append('advanceUnlockScore', advanceUnlockScore);
    data.append('subtext', subtext);
    if (parseInt(contentType, 10) === CONTENT_TYPE_ID.ROCKED) {
      data.append('rockedNetworkPartnerId', rnpList.ROCKED.partners[0].id);
    } else {
      data.append('rockedNetworkPartnerId', selectedRnp);
    }
    departmentIds.forEach((item) => {
      data.append('departmentIds', item);
    });
    if (!id || (id && imageData instanceof File)) {
      data.append('thumbnail', imageData);
    }
    if (id) {
      data.append('id', id);
    }

    try {
      await service.post(apiEndpoints.AI_CREATE_CATEGORY, data);
      notifySuccess('Exercise category created successfully');
      fetchAllCategories();
      onClose();
    } catch (error) {
      ErrorToast(error);
    }
    dispatch(showProgress(false));
  };

  const fetchAllCategories = async () => {
    dispatch(showProgress(true));
    try {
      const res = await service.get(apiEndpoints.AI_EXERCISE_CATEGORIES);
      dispatch(setExerciseCategories(res));
      if (!_isEmpty(res)) {
        handleListItemClick(res[0]);
      }
    } catch (error) {
      ErrorToast(error);
    }
    dispatch(showProgress(false));
  };

  const fetchAllAiRolePlayDepartments = async () => {
    dispatch(showProgress(true));
    try {
      const res = await service.get(apiEndpoints.AI_GET_EXERCISE_DEPARTMENTS);
      setDepartmentsList(res);
    } catch (error) {
      ErrorToast(error);
    }
    dispatch(showProgress(false));
  };

  const handleListItemClick = async (data = {}) => {
    dispatch(showProgress(true));

    if (!_isEmpty(data)) {
      dispatch(setExerciseCategory(data));
    }
    dispatch(showProgress(false));
  };

  const fetchCategoryInfo = async () => {
    dispatch(showProgress(true));

    try {
      const res = await service.get(apiEndpoints.AI_TOPIC_INFO.replace('ID', selectedCat.id));
      setForm(res);
      setThumbnail(res.thumbnail);
      setContentType(res.contentType);
      setSelectedRnp(res.rockedNetworkPartnerId);
    } catch (error) {
      ErrorToast(error);
    }
    dispatch(showProgress(false));
  };

  const deleteCategory = async () => {
    dispatch(showProgress(true));
    try {
      await service.delete(apiEndpoints.AI_DELETE_TOPIC.replace('ID', selectedCat.id));
      notifySuccess('Exercise category deleted successfully');
      fetchAllCategories();
    } catch (error) {
      ErrorToast(error);
    }
    dispatch(showProgress(false));
  };

  const resetAll = () => {
    setForm(INITIAL_EXERCISE_CATEGORY_FORM);
    setImageData();
    setThumbnail('');
    setThumbnailImg(null);
  };

  const fetchRockedNetworkPartners = async () => {
    dispatch(showProgress(true));
    try {
      const res = await service.get(apiEndpoints.AI_GET_ROCKED_NETWORK_PARTNERS);
      const transformedRes = _keyBy(res, 'type');
      setRnpList(transformedRes);
    } catch (error) {
      ErrorToast(error);
    } finally {
      dispatch(showProgress(false));
    }
  };

  return {
    form,
    handleForm,
    handleSave,
    handleImageChange,
    handleDeleteThumbnail,
    imageData,
    thumbnail,
    thumbnailImg,
    rnpList,
    selectedRnp,
    contentType,
    fetchAllCategories,
    handleListItemClick,
    fetchCategoryInfo,
    deleteCategory,
    resetAll,
    fetchAllAiRolePlayDepartments,
    departmentsList,
    handleDepartmentChange,
    fetchRockedNetworkPartners,
    setSelectedRnp,
    setContentType,
  };
};
